import { AxiosInstance } from 'axios';

import AIModel, { ModelFiles, UpdateModelParams } from 'types/ai_model';
import AccessToken from 'types/token';
import Application from 'types/application';
import Component from 'pipelines/services/rete/nodes/component';
import Deployment from 'types/deployment';
import LumeoJWT from 'types/jwt';
import Pipeline from 'types/pipeline';
import { Account } from 'types/account';
import { Camera } from 'types/camera';
import { Stream } from 'types/stream';
import { Tag } from 'types/tag';
import {
  AcceptAndCreateAccountVariables,
  InvitationVariables,
} from 'hooks/api/useInvitations';
import { AppEntityObject, TaggableEntityObject } from 'hooks/useEntities';
import { CreateAccessTokenVariables } from 'hooks/api/useAccessTokens';
import { CreatePipelineVariables } from 'pipelines/hooks/useCreatePipeline';
import { Gateway } from 'types/gateway';
import { GatewayConfiguration } from 'types/gateway_configuration';
import { GatewaySpecification } from 'types/gateway_specification';
import { JwtType, JwtParams } from 'hooks/useJWT';
import { LumeoFile } from 'types/file';
import { LumeoUploadFile } from 'files/hooks/useUploadFile';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';
import { PaginatedAPIResponse } from 'types/api';
import { UpdatePasswordParams } from 'account/components/ChangePasswordForm/ChangePasswordForm';
import { UpdatePipelineVariables } from 'pipelines/hooks/useUpdatePipeline';
import { UseCamerasParams } from 'cameras/hooks/useCameras';
import { UseCreateGatewayConfigurationParams } from 'gateways/hooks/useCreateGatewayConfiguration';
import { UseDeploymentsFilterParams } from 'hooks/api/useDeployments';
import { UseGatewaysParams } from 'gateways/hooks/useGateways';
import { UsePipelinesFilterParams } from 'pipelines/hooks/usePipelines';
import { UseServerVersionResponse } from 'hooks/api/useServerVersion';
import { UseSnapshotResponse } from 'hooks/useSnapshot';
import { UseStreamsParams } from 'streams/hooks/useStreams';
import { UseUpdateGatewayConfigurationParams } from 'gateways/hooks/useUpdateGatewayConfiguration';
import { VideoSource } from 'hooks/api/useVideoSources';

export const API_BASE_URL = `${import.meta.env.VITE_API_URL}/v1`;

type CrudAPI<T, TId = string | undefined, TParams = Record<string, any>> = {
  list: (params?: TParams) => Promise<T[]>;
  create: (el: T) => Promise<T>;
  update: (el: T) => Promise<T>;
  delete: (el: TId) => Promise<void>;
};

/** Updated type for paginated endpoints */
type CommonCRUDEndpoints<T extends AppEntityObject, TParams> = {
  list: (
    params: OffsetPaginationParams & TParams
  ) => Promise<PaginatedAPIResponse<T>>;
  read: (el: T['id']) => Promise<T>;
  create: (el: T) => Promise<T>;
  update: (el: T) => Promise<T>;
  delete: (el: T['id']) => Promise<void>;
};

type CRUDEndpointsNotTaggable<
  T extends AppEntityObject,
  TParams,
> = CommonCRUDEndpoints<T, TParams> & {
  addTags?: never;
  removeTags?: never;
};

type CRUDEndpointsTaggable<
  T extends TaggableEntityObject,
  TParams,
> = CommonCRUDEndpoints<T, TParams> & {
  addTags: (el: T['id'], tagIds: Tag['id'][]) => Promise<Tag['id'][]>;
  removeTags: (el: T['id'], tagIds: Tag['id'][]) => Promise<Tag['id'][]>;
};

type CRUDEndpoints<
  T extends AppEntityObject,
  TParams,
> = T extends TaggableEntityObject
  ? CRUDEndpointsTaggable<T, TParams>
  : CRUDEndpointsNotTaggable<T, TParams>;

/** @deprecated Define your fetch function in the queryFn prop of useQuery */
export type APIObject = {
  authToken?: string;
  applicationID?: string;
  setApplicationID: (applicationID: string) => void;
  client: AxiosInstance;

  sendBeacon: (url: string, data?: URLSearchParams) => void;

  /** @deprecated */
  internal: {
    readServerVersion: () => Promise<UseServerVersionResponse>;
  };

  /** @deprecated */
  applications: CrudAPI<Application> & {
    read: (applicationID: string) => Promise<Application | undefined>;

    gatewayConfiguration: {
      read: (gatewayConfigurationId: string) => Promise<GatewayConfiguration>;
      list: (
        params: OffsetPaginationParams
      ) => Promise<OffsetPaginated<GatewayConfiguration>>;
      create: (
        params: UseCreateGatewayConfigurationParams
      ) => Promise<GatewayConfiguration>;
      update: (
        params: UseUpdateGatewayConfigurationParams
      ) => Promise<GatewayConfiguration>;
    };
  };

  /** @deprecated */
  accounts: {
    read: (accountID: string) => Promise<Account | undefined>;
    update: (account: Partial<Account>) => Promise<Account>;
    updatePassword: (
      accountID: string,
      { oldPassword, newPassword }: UpdatePasswordParams
    ) => Promise<any>;
  };

  /** @deprecated */
  accessTokens: {
    list: () => Promise<AccessToken[]>;
    create: (variables: CreateAccessTokenVariables) => Promise<AccessToken>;
    delete: (id: AccessToken['id']) => Promise<void>;
  };

  /** @deprecated */
  jwt: {
    create: <Type extends JwtType>(
      variables: JwtParams<Type>
    ) => Promise<LumeoJWT>;
    createSearchToken: () => Promise<LumeoJWT>;
  };

  /** @deprecated */
  pipelines: Omit<
    CRUDEndpoints<Pipeline, UsePipelinesFilterParams>,
    'create' | 'update'
  > & {
    create: (variables: CreatePipelineVariables) => Promise<Pipeline>;
    update: (variables: UpdatePipelineVariables) => Promise<Pipeline>;
    deploy: (deployment: Deployment) => Promise<Deployment>;
    lock: (pipelineID: string) => Promise<void>;
    unlock: (pipelineID: string) => Promise<void>;

    dynamicComponents: {
      list: () => Promise<Component[]>;
    };
  };

  /** @deprecated */
  deployments: Omit<
    CRUDEndpoints<Deployment, UseDeploymentsFilterParams>,
    'create'
  > & {
    start: (deploymentID: Deployment['id']) => Promise<any>;
    stop: (deploymentID: Deployment['id']) => Promise<any>;
    rename: (
      deploymentID: Deployment['id'],
      name: string
    ) => Promise<Deployment>;
  };

  /** @deprecated */
  streams: Pick<
    CRUDEndpoints<Stream, UseStreamsParams>,
    'addTags' | 'removeTags'
  >;
  /** @deprecated */
  cameras: Pick<
    CRUDEndpoints<Camera, UseCamerasParams>,
    'addTags' | 'removeTags'
  >;
  /** @deprecated */
  snapshot: (source: VideoSource) => Promise<UseSnapshotResponse>;
  /** @deprecated */
  files: {
    create: (file: LumeoUploadFile) => Promise<LumeoFile>;
    read: (fileID: string) => Promise<LumeoFile>;
    updateStatus: (
      fileID: string,
      cloud_status: LumeoFile['cloud_status']
    ) => Promise<void>;
    addTags: (
      fileId: LumeoFile['id'],
      tagIds: Tag['id'][]
    ) => Promise<Tag['id'][]>;
    removeTags: (
      fileId: LumeoFile['id'],
      tagIds: Tag['id'][]
    ) => Promise<Tag['id'][]>;
  };
  /** @deprecated */
  gateways: Pick<
    CRUDEndpoints<Gateway, UseGatewaysParams>,
    'addTags' | 'removeTags'
  > & {
    getSpecification: (
      gatewayID: Gateway['id']
    ) => Promise<GatewaySpecification | null>;
    cloud: {
      create: (name: string, type?: 'trial') => Promise<Gateway>;
      start: (gatewayID: Gateway['id']) => Promise<void>;
      stop: (gatewayID: Gateway['id']) => Promise<void>;
      retry: (gatewayID: Gateway['id']) => Promise<void>;
      restart: (gatewayID: Gateway['id']) => Promise<void>;
    };
  };
  /** @deprecated */
  models: Pick<CrudAPI<AIModel, AIModel['id']>, 'list' | 'delete'> & {
    read: (modelID: AIModel['id']) => Promise<AIModel>;
    create: (model: AIModel, files: ModelFiles) => Promise<AIModel>;
    update: (model: UpdateModelParams, files?: ModelFiles) => Promise<AIModel>;
    marketplace: Pick<CrudAPI<AIModel, AIModel['id']>, 'list' | 'delete'> & {
      read: (modelID: AIModel['id']) => Promise<AIModel>;
      /** admin-only */
      create: (model: AIModel, files: ModelFiles) => Promise<AIModel>;
      update: (model: UpdateModelParams, files: ModelFiles) => Promise<AIModel>;
    };
  };
  /** @deprecated */
  invitations: {
    accept: (variables: InvitationVariables) => Promise<void>;
    acceptAndCreateAccount: (
      variables: AcceptAndCreateAccountVariables
    ) => Promise<void>;
    decline: (variables: InvitationVariables) => Promise<void>;
    hasAccount: (token: string) => Promise<void>;
  };
  /** @deprecated */
  webrtc: {
    getOffer: (streamID: string) => Promise<any>;
    close: (streamID: string, peerID: string) => Promise<any>;
    setAnswer: (streamID: string, peerID: string, data: any) => Promise<any>;
    getCandidates: (streamID: string, peerID: string) => Promise<any>;
    addCandidate: (
      streamID: string,
      peerID: string,
      candidate: { toJSON: () => any }
    ) => Promise<any>;
  };
};
